import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #ffffff;
  padding: 40px 24px;
`;

const Card = styled.div`
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 48px 40px;
  width: 100%;
  max-width: 420px;
  animation: ${fadeIn} 0.3s ease;
`;

const Title = styled.h1`
  color: #fff;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0 0 8px;
  letter-spacing: -0.5px;
  span { color: #fe88f1; }
`;

const Subtitle = styled.p`
  color: rgba(255,255,255,0.4);
  text-align: center;
  font-size: 0.88rem;
  margin: 0 0 32px;
  line-height: 1.5;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.4);
  margin-bottom: 7px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255,255,255,0.05);
  border: 1.5px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: #fff;
  font-size: 0.95rem;
  font-family: inherit;
  box-sizing: border-box;
  margin-bottom: 18px;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: rgba(254,136,241,0.5); }
  &::placeholder { color: rgba(255,255,255,0.2); }
`;

const Hint = styled.p`
  color: rgba(255,255,255,0.28);
  font-size: 0.78rem;
  margin: -12px 0 18px;
  line-height: 1.4;
`;

const Btn = styled.button`
  width: 100%;
  padding: 13px;
  background: #fe88f1;
  color: #0d0d0d;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  &:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SuccessBox = styled.div`
  background: rgba(0,210,100,0.1);
  border: 1px solid rgba(0,210,100,0.25);
  border-radius: 10px;
  padding: 18px;
  text-align: center;
  animation: ${fadeIn} 0.3s ease;
`;

const SuccessText = styled.p`
  color: #4dde99;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0 0 12px;
`;

const ErrorMsg = styled.p`
  color: #ff7e7e;
  font-size: 0.85rem;
  text-align: center;
  margin: 0 0 12px;
`;

const InvalidBox = styled.div`
  text-align: center;
  padding: 8px 0;
`;

const InvalidText = styled.p`
  color: rgba(255,255,255,0.5);
  font-size: 0.9rem;
  margin: 0 0 12px;
  line-height: 1.5;
`;

const BackLink = styled.p`
  color: rgba(255,255,255,0.4);
  font-size: 0.85rem;
  text-align: center;
  margin-top: 20px;
  a { color: #fe88f1; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const SignInBtn = styled(Link)`
  display: inline-block;
  margin-top: 4px;
  padding: 11px 28px;
  background: #fe88f1;
  color: #0d0d0d;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 700;
  text-decoration: none;
  transition: opacity 0.2s;
  &:hover { opacity: 0.88; }
`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  // No token in URL — show helpful error
  if (!token) {
    return (
      <Container>
        <Helmet><title>Reset Password — MyCinemaDB</title></Helmet>
        <Card>
          <Title>My<span>Cinema</span>DB</Title>
          <InvalidBox>
            <InvalidText>
              ⚠️ This reset link is invalid or has expired.<br />
              Request a new one from the sign-in page.
            </InvalidText>
            <SignInBtn to="/forgot-password">Request new link</SignInBtn>
          </InvalidBox>
        </Card>
      </Container>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!/[0-9!@#$%^&*]/.test(password)) {
      setError('Password must contain at least one number or special character.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Reset failed');
      setDone(true);
      // Auto-redirect to sign-in after 3 s
      setTimeout(() => navigate('/sign-in?reset=1'), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Helmet>
        <title>Reset Password — MyCinemaDB</title>
        <meta name="description" content="Set a new password for your MyCinemaDB account." />
      </Helmet>

      <Card>
        <Title>My<span>Cinema</span>DB</Title>
        <Subtitle>Choose a new password for your account.</Subtitle>

        {done ? (
          <SuccessBox>
            <SuccessText>✅ Password updated!</SuccessText>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', margin: '0 0 12px' }}>
              Redirecting you to sign in…
            </p>
            <SignInBtn to="/sign-in">Sign In now</SignInBtn>
          </SuccessBox>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && <ErrorMsg>{error}</ErrorMsg>}

            <FieldLabel htmlFor="rp-password">New password</FieldLabel>
            <Input
              id="rp-password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              required
              autoComplete="new-password"
              autoFocus
            />
            <Hint>At least 8 characters with one number or special character.</Hint>

            <FieldLabel htmlFor="rp-confirm">Confirm new password</FieldLabel>
            <Input
              id="rp-confirm"
              type="password"
              placeholder="••••••••"
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setError(''); }}
              required
              autoComplete="new-password"
            />

            <Btn type="submit" disabled={loading}>
              {loading ? 'Saving…' : 'Set New Password'}
            </Btn>
          </form>
        )}

        <BackLink>
          <Link to="/sign-in">Back to Sign In</Link>
        </BackLink>
      </Card>
    </Container>
  );
};

export default ResetPassword;
